import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box, Flex, Text, Badge, Avatar, AvatarGroup, Tooltip, HStack, VStack,
    IconButton, Spinner, Modal, ModalOverlay, ModalContent, ModalHeader,
    ModalFooter, ModalBody, ModalCloseButton, Select, Button, Divider, Tag,
    FormControl, FormLabel, useToast, useBreakpointValue, Icon, keyframes
} from '@chakra-ui/react';
import { FaExternalLinkAlt, FaPencilAlt, FaDownload, FaFire, FaArrowUp, FaMinus, FaArrowDown, FaBell } from 'react-icons/fa';
import TicketNotifyDialog from '../TicketNotifyDialog';
import { WikiContentViewer } from '../WikiEditor';
import { MdOutlineNewReleases, MdBuild, MdBlock, MdCheckCircle, MdAccessTimeFilled, MdCalendarToday } from 'react-icons/md';
import { formatDistanceToNowStrict, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getTicket, updateTicket, downloadTicketAttachment, reorderTickets } from '../../services/api';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import confetti from 'canvas-confetti';

const formatDistance = (dateString) => {
    if (!dateString) return '-';
    return formatDistanceToNowStrict(new Date(dateString), { addSuffix: true, locale: ptBR });
};
const formatDateShort = (dateString) => {
    if (!dateString) return '-';
    return format(new Date(dateString), "dd/MM/yy", { locale: ptBR });
};

const formatDateTimeShort = (dateString) => {
    if (!dateString) return null;
    return format(new Date(dateString), "dd/MM/yy HH:mm", { locale: ptBR });
};

const statusConfig = {
    aberto: { label: 'Aberto', icon: MdOutlineNewReleases, color: 'blue', headerGradient: 'linear(to-r, #3B82F6, #2563EB)', bg: '#F0F7FF', border: '#BFDBFE', cardAccent: '#3B82F6', dropGlow: 'rgba(59,130,246,0.15)' },
    em_andamento: { label: 'Em Andamento', icon: MdBuild, color: 'orange', headerGradient: 'linear(to-r, #F97316, #EA580C)', bg: '#FFF7ED', border: '#FED7AA', cardAccent: '#F97316', dropGlow: 'rgba(249,115,22,0.15)' },
    bloqueado: { label: 'Bloqueado', icon: MdBlock, color: 'purple', headerGradient: 'linear(to-r, #8B5CF6, #7C3AED)', bg: '#FAF5FF', border: '#D8B4FE', cardAccent: '#8B5CF6', dropGlow: 'rgba(139,92,246,0.15)' },
    fechado: { label: 'Concluido', icon: MdCheckCircle, color: 'green', headerGradient: 'linear(to-r, #22C55E, #16A34A)', bg: '#F0FDF4', border: '#BBF7D0', cardAccent: '#22C55E', dropGlow: 'rgba(34,197,94,0.15)' },
};

const priorityConfig = {
    urgente: { color: 'red', label: 'Urgente', icon: FaFire, iconColor: '#EF4444', borderColor: '#FCA5A5', bgGlow: 'rgba(239,68,68,0.05)' },
    alta: { color: 'orange', label: 'Alta', icon: FaArrowUp, iconColor: '#F97316', borderColor: '#FDBA74', bgGlow: 'rgba(249,115,22,0.03)' },
    media: { color: 'yellow', label: 'Media', icon: FaMinus, iconColor: '#EAB308', borderColor: null, bgGlow: null },
    baixa: { color: 'green', label: 'Baixa', icon: FaArrowDown, iconColor: '#22C55E', borderColor: null, bgGlow: null },
};

const statusOrder = ['aberto', 'em_andamento', 'bloqueado', 'fechado'];

const getImgUser = (usuario_id) => {
    const map = { 3: "/gabriel_logo.webp", 5: "/tilio_logo.webp", 6: "/riccioppo_logo.webp", 7: "/user.png", 8: "/gabriel_valiente_logo.webp", 9: "/emerson_vendedor_logo.webp", 10: "/analuiza_logo.webp" };
    return map[usuario_id] || "/user.png";
};

/* ── Animations ── */
const pulseGlow = keyframes`0% { box-shadow: 0 0 0 0 rgba(59,130,246,0.4); } 70% { box-shadow: 0 0 0 10px rgba(59,130,246,0); } 100% { box-shadow: 0 0 0 0 rgba(59,130,246,0); }`;

const fireStatusAnimation = (newStatus) => {
    if (newStatus === 'fechado') {
        const count = 200;
        const defaults = { origin: { y: 0.7 }, zIndex: 9999 };
        confetti({ ...defaults, spread: 80, particleCount: count * 0.3, scalar: 1.2 });
        confetti({ ...defaults, spread: 100, particleCount: count * 0.2 });
        confetti({ ...defaults, spread: 120, particleCount: count * 0.35, decay: 0.91, scalar: 0.8 });
        confetti({ ...defaults, spread: 60, particleCount: count * 0.15, startVelocity: 25, decay: 0.92, scalar: 1.2 });
    }
    // Other statuses: no confetti - just toast feedback
};

/* ── Card inner content (reusable) ── */
const CardContent = ({ ticket, currentUser, onOpenModal, statusAccent, pConfig, dragHandleProps, isDragging, isDragDisabled }) => {
    const canEdit = currentUser && ticket.author && currentUser.id === ticket.author.id;
    return (
        <div
            style={{
                background: 'white',
                borderRadius: '8px',
                overflow: 'hidden',
                border: `1px solid ${isDragging ? statusAccent : (pConfig.borderColor || '#E5E7EB')}`,
                boxShadow: isDragging ? '0 16px 32px rgba(0,0,0,0.25)' : '0 1px 2px rgba(0,0,0,0.04)',
                cursor: 'pointer',
                width: '100%',
            }}
            onClick={() => !isDragging && onOpenModal(ticket)}
        >
            <Flex
                {...dragHandleProps}
                align="center" justify="space-between"
                bg={isDragging ? `${statusAccent}18` : 'gray.50'}
                borderBottom="1px solid" borderColor={isDragging ? `${statusAccent}40` : 'gray.100'}
                px={2} py={1}
                cursor={isDragDisabled ? "pointer" : "grab"}
                _hover={!isDragDisabled ? { bg: 'gray.100' } : {}}
                transition="background 0.15s"
            >
                <HStack spacing={1.5}>
                    <Box w="3px" h="12px" bg={pConfig.iconColor} borderRadius="full" />
                    <Text fontSize="10px" color="gray.500" fontFamily="mono" fontWeight="bold">#{ticket.id}</Text>
                    <Badge colorScheme={pConfig.color} fontSize="9px" textTransform="capitalize" borderRadius="full" px={1.5} display="flex" alignItems="center" gap="2px">
                        <Icon as={pConfig.icon} boxSize="9px" />{pConfig.label}
                    </Badge>
                </HStack>
                <HStack spacing={0} flexShrink={0} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                    {canEdit && (
                        <IconButton as={RouterLink} to={`/tickets/${ticket.id}/editar`} variant="ghost" size="xs" minW="20px" h="20px" icon={<FaPencilAlt size={9} />} colorScheme="yellow" aria-label="Editar" borderRadius="full" />
                    )}
                    <IconButton as={RouterLink} to={`/tickets/${ticket.id}`} target="_blank" rel="noopener noreferrer" variant="ghost" size="xs" minW="20px" h="20px" icon={<FaExternalLinkAlt size={9} />} colorScheme="blue" aria-label="Detalhes" borderRadius="full" />
                </HStack>
            </Flex>
            <Box px={2} py={1.5}>
                <Text fontWeight="600" fontSize="11px" noOfLines={2} lineHeight="1.3" color="gray.800">{ticket.subject}</Text>
                <Flex align="center" justify="space-between" mt={1}>
                    <HStack spacing={1.5} flex={1} overflow="hidden">
                        {ticket.categories?.length > 0 && <Text fontSize="10px" color="gray.400" isTruncated>{ticket.categories.map(c => c.name).join(', ')}</Text>}
                    </HStack>
                </Flex>
                <Flex align="center" justify="space-between" mt={1}>
                    <HStack spacing={1.5}>
                        <HStack spacing={1}>
                            <Icon as={MdCalendarToday} boxSize="9px" color="gray.400" />
                            <Text fontSize="9px" color="gray.500">{ticket.status === 'aberto' ? formatDateTimeShort(ticket.created_at) : formatDateShort(ticket.created_at)}</Text>
                        </HStack>
                        <HStack spacing={1}>
                            <Icon as={MdAccessTimeFilled} boxSize="9px" color="orange.400" />
                            <Text fontSize="9px" color="orange.500">{formatDistance(ticket.created_at)}</Text>
                        </HStack>
                    </HStack>
                </Flex>
                {ticket.status !== 'aberto' && ticket.status_changed_at && (
                    <HStack spacing={1} mt={1}>
                        <Icon as={statusConfig[ticket.status]?.icon} boxSize="9px" color={statusConfig[ticket.status]?.cardAccent} />
                        <Text fontSize="9px" color="gray.500" isTruncated>
                            {statusConfig[ticket.status]?.label} em {formatDateTimeShort(ticket.status_changed_at)}
                        </Text>
                    </HStack>
                )}
                <Flex align="center" justify="space-between" mt={1}>
                    <HStack spacing={1.5}>
                        <Avatar boxSize="20px" name={ticket.author?.name} src={getImgUser(ticket.author?.id)} />
                        <Text fontSize="10px" color="gray.400">{ticket.author?.name?.split(' ')[0]}</Text>
                    </HStack>
                    {ticket.assignees?.length > 0 && (
                        <AvatarGroup max={3} spacing="-4px">
                            {ticket.assignees.map(user => (
                                <Tooltip key={user.id} label={user.name} placement="top" hasArrow><Avatar boxSize="24px" name={user.name} src={getImgUser(user.id)} /></Tooltip>
                            ))}
                        </AvatarGroup>
                    )}
                </Flex>
            </Box>
        </div>
    );
};

/* ── Draggable Card with Portal ── */
const portalNode = typeof document !== 'undefined' ? document.body : null;

const TicketCard = ({ ticket, currentUser, onOpenModal, statusAccent, index, isDragDisabled }) => {
    const pConfig = priorityConfig[ticket.priority] || priorityConfig.media;

    return (
        <Draggable draggableId={String(ticket.id)} index={index} isDragDisabled={isDragDisabled}>
            {(provided, snapshot) => {
                const child = (
                    <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                            ...provided.draggableProps.style,
                            marginBottom: snapshot.isDragging ? '0px' : '6px',
                        }}
                    >
                        <CardContent
                            ticket={ticket}
                            currentUser={currentUser}
                            onOpenModal={onOpenModal}
                            statusAccent={statusAccent}
                            pConfig={pConfig}
                            dragHandleProps={provided.dragHandleProps}
                            isDragging={snapshot.isDragging}
                            isDragDisabled={isDragDisabled}
                        />
                    </div>
                );

                // When dragging, render via portal to escape any overflow/transform parent
                if (snapshot.isDragging && portalNode) {
                    return ReactDOM.createPortal(child, portalNode);
                }
                return child;
            }}
        </Draggable>
    );
};

/* ── Droppable Column ── */
const KanbanColumn = ({ status, tickets, currentUser, onOpenModal, isMobile, isDraggingGlobal, draggingFromStatus }) => {
    const config = statusConfig[status];
    const count = tickets.length;
    const StatusIcon = config.icon;
    const isTargetColumn = isDraggingGlobal && draggingFromStatus !== status;

    return (
        <Box
            minW={isMobile ? "280px" : "0"}
            w={isMobile ? "280px" : "100%"}
            flex={isMobile ? "0 0 280px" : 1}
            display="flex"
            flexDirection="column"
            h={isMobile ? "calc(100vh - 120px)" : "calc(100vh - 80px)"}
            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
            transform={isTargetColumn ? 'scale(1.01)' : 'scale(1)'}
        >
            <Flex
                bgGradient={config.headerGradient}
                color="white" px={3} py={2}
                borderRadius="lg lg 0 0"
                align="center" justify="space-between"
                boxShadow={isTargetColumn ? `0 0 25px ${config.cardAccent}40, 0 4px 15px rgba(0,0,0,0.15)` : '0 2px 8px rgba(0,0,0,0.1)'}
                transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
            >
                <HStack spacing={1.5}>
                    <Icon as={StatusIcon} boxSize={isTargetColumn ? "18px" : "14px"} transition="all 0.3s" />
                    <Text fontWeight="700" fontSize="xs">{config.label}</Text>
                </HStack>
                <Flex bg="whiteAlpha.200" borderRadius="full" px={2} py={0.5} minW="24px" justify="center">
                    <Text fontSize="xs" fontWeight="800">{count}</Text>
                </Flex>
            </Flex>

            <Droppable droppableId={status} isDropDisabled={isMobile}>
                {(provided, snapshot) => (
                    <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        bg={snapshot.isDraggingOver ? config.dropGlow : (isDraggingGlobal && !isTargetColumn ? 'gray.100' : config.bg)}
                        border="2px solid"
                        borderColor={snapshot.isDraggingOver ? config.cardAccent : (isTargetColumn ? `${config.cardAccent}80` : config.border)}
                        borderTop="none"
                        borderRadius="0 0 lg lg"
                        flex={1}
                        overflowY="auto"
                        p={1.5}
                        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                        boxShadow={snapshot.isDraggingOver ? `inset 0 0 40px ${config.dropGlow}, 0 0 20px ${config.cardAccent}30` : (isTargetColumn ? `inset 0 0 15px ${config.dropGlow}` : 'none')}
                        opacity={isDraggingGlobal && !isTargetColumn && !snapshot.isDraggingOver ? 0.5 : 1}
                        css={{
                            '&::-webkit-scrollbar': { width: '4px' },
                            '&::-webkit-scrollbar-track': { background: 'transparent' },
                            '&::-webkit-scrollbar-thumb': { background: config.border, borderRadius: '10px' },
                        }}
                    >
                        {tickets.length === 0 && !snapshot.isDraggingOver && !isTargetColumn ? (
                            <Flex justify="center" align="center" py={10} flexDirection="column" gap={1}>
                                <Icon as={StatusIcon} boxSize="24px" color="gray.300" />
                                <Text fontSize="xs" color="gray.400" fontStyle="italic">Nenhum ticket</Text>
                            </Flex>
                        ) : (
                            tickets.map((ticket, index) => (
                                <TicketCard
                                    key={ticket.id}
                                    ticket={ticket}
                                    currentUser={currentUser}
                                    onOpenModal={onOpenModal}
                                    statusAccent={config.cardAccent}
                                    index={index}
                                    isDragDisabled={isMobile}
                                />
                            ))
                        )}
                        {provided.placeholder}
                        {(snapshot.isDraggingOver || (isTargetColumn && tickets.length === 0)) && (
                            <Flex
                                justify="center" align="center" py={4} flexDirection="column" gap={1}
                                borderRadius="lg" border="2px dashed" borderColor={config.cardAccent}
                                mx={1} mt={tickets.length > 0 ? 1 : 0}
                                animation={snapshot.isDraggingOver ? `${pulseGlow} 1.5s infinite` : undefined}
                                bg={snapshot.isDraggingOver ? `${config.cardAccent}10` : 'transparent'}
                                transition="all 0.2s"
                            >
                                <Icon as={StatusIcon} boxSize="20px" color={config.cardAccent} />
                                <Text fontSize="xs" color={config.cardAccent} fontWeight="600">
                                    {snapshot.isDraggingOver ? 'Solte para mover' : 'Arraste aqui'}
                                </Text>
                            </Flex>
                        )}
                    </Box>
                )}
            </Droppable>
        </Box>
    );
};

/* ── Main Component ── */
const TicketKanbanView = ({ tickets: ticketsProp, loading, onUpdateSuccess }) => {
    const [localTickets, setLocalTickets] = useState(ticketsProp);
    const [currentUser, setCurrentUser] = useState(null);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [modalDetails, setModalDetails] = useState(null);
    const [isModalLoading, setIsModalLoading] = useState(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [modalStatus, setModalStatus] = useState('');
    const [downloadingId, setDownloadingId] = useState(null);
    const [isNotifyOpen, setIsNotifyOpen] = useState(false);
    const [isDraggingGlobal, setIsDraggingGlobal] = useState(false);
    const [draggingFromStatus, setDraggingFromStatus] = useState(null);
    const toast = useToast();
    const isMobile = useBreakpointValue({ base: true, lg: false });

    useEffect(() => { setLocalTickets(ticketsProp); }, [ticketsProp]);
    useEffect(() => {
        const userString = window.localStorage.getItem("user");
        if (userString) { try { setCurrentUser(JSON.parse(userString)); } catch {} }
    }, []);

    const byPos = (a, b) => {
        const pa = a.position == null ? -Infinity : a.position;
        const pb = b.position == null ? -Infinity : b.position;
        if (pa !== pb) return pa - pb;
        return new Date(b.created_at) - new Date(a.created_at);
    };

    const groupedTickets = statusOrder.reduce((acc, status) => {
        acc[status] = localTickets.filter(t => t.status === status).sort(byPos);
        return acc;
    }, {});

    /* ── Drag & Drop Handler ── */
    const handleDragStart = useCallback((start) => {
        setIsDraggingGlobal(true);
        setDraggingFromStatus(start.source.droppableId);
    }, []);

    const handleDragEnd = useCallback(async (result) => {
        setIsDraggingGlobal(false);
        setDraggingFromStatus(null);
        const { draggableId, source, destination } = result;
        if (!destination || (source.droppableId === destination.droppableId && source.index === destination.index)) return;

        const ticketId = parseInt(draggableId);
        const newStatus = destination.droppableId;
        const oldStatus = source.droppableId;
        const statusChanged = oldStatus !== newStatus;

        const prevTickets = localTickets;

        // reconstroi as colunas na ordem atual e aplica o movimento
        const cols = {};
        statusOrder.forEach(s => { cols[s] = localTickets.filter(t => t.status === s).sort(byPos).map(t => ({ ...t })); });

        const [moved] = cols[oldStatus].splice(source.index, 1);
        if (!moved) return;
        moved.status = newStatus;
        cols[newStatus].splice(destination.index, 0, moved);

        // recalcula posicoes nas colunas afetadas
        const affected = statusChanged ? [oldStatus, newStatus] : [newStatus];
        const orderItems = [];
        affected.forEach(s => {
            cols[s].forEach((t, i) => { t.position = i; orderItems.push({ id: t.id, position: i }); });
        });

        // atualizacao otimista
        const affectedIds = new Set(orderItems.map(o => o.id));
        const untouched = localTickets.filter(t => !affectedIds.has(t.id));
        setLocalTickets([...untouched, ...affected.flatMap(s => cols[s])]);

        if (statusChanged) fireStatusAnimation(newStatus);

        try {
            if (statusChanged) {
                const formData = new FormData();
                formData.append('_method', 'PUT');
                formData.append('status', newStatus);
                if (moved?.category?.id) formData.append('categoryIds[]', moved.category.id);
                await updateTicket(ticketId, formData);
            }
            await reorderTickets(orderItems);
            if (statusChanged) {
                toast({ title: `Ticket #${ticketId} movido para ${statusConfig[newStatus]?.label}`, status: "success", duration: 2000, isClosable: true });
            }
        } catch (error) {
            setLocalTickets(prevTickets);
            toast({ title: "Erro ao mover ticket", description: error.response?.data?.message || "", status: "error", isClosable: true });
        }
    }, [localTickets, toast]);

    const handleOpenModal = async (ticket) => {
        setSelectedTicket(ticket);
        setIsModalLoading(true);
        try {
            const response = await getTicket(ticket.id);
            setModalDetails(response.data.data);
            setModalStatus(response.data.data.status);
        } catch {
            toast({ title: "Erro ao buscar detalhes.", status: "error", isClosable: true });
            handleCloseModal();
        } finally { setIsModalLoading(false); }
    };

    const handleCloseModal = () => { setSelectedTicket(null); setModalDetails(null); setModalStatus(''); };

    const handleUpdateStatus = async () => {
        if (!selectedTicket || !modalStatus || !modalDetails) return;
        setIsUpdatingStatus(true);
        try {
            const formData = new FormData();
            formData.append('_method', 'PUT');
            formData.append('status', modalStatus);
            if (modalDetails.categories?.length > 0) {
                modalDetails.categories.forEach(c => formData.append('categoryIds[]', c.id));
            }
            await updateTicket(selectedTicket.id, formData);
            setLocalTickets(prev => prev.map(t => t.id === selectedTicket.id ? { ...t, status: modalStatus } : t));
            fireStatusAnimation(modalStatus);
            toast({ title: "Status atualizado!", status: "success", isClosable: true });
            handleCloseModal();
            if (onUpdateSuccess) onUpdateSuccess();
        } catch (error) {
            toast({ title: "Erro ao atualizar.", description: error.response?.data?.message || "", status: "error", isClosable: true });
        } finally { setIsUpdatingStatus(false); }
    };

    const handleDownloadAttachment = async (fileId, fileName) => {
        setDownloadingId(fileId);
        try {
            const response = await downloadTicketAttachment(fileId);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch { toast({ title: "Erro no Download", status: "error", isClosable: true }); }
        finally { setDownloadingId(null); }
    };

    if (loading) return <Flex justify="center" align="center" py={20}><Spinner size="xl" color="orange.400" thickness="3px" /></Flex>;

    return (
        <>
            <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                <Flex
                    gap={2}
                    overflowX={isMobile ? "auto" : "hidden"}
                    pb={4}
                    css={isMobile ? { '&::-webkit-scrollbar': { height: '6px' }, '&::-webkit-scrollbar-thumb': { background: '#CBD5E0', borderRadius: '10px' } } : {}}
                >
                    {statusOrder.map(status => (
                        <KanbanColumn
                            key={status}
                            status={status}
                            tickets={groupedTickets[status]}
                            currentUser={currentUser}
                            onOpenModal={handleOpenModal}
                            isMobile={isMobile}
                            isDraggingGlobal={isDraggingGlobal}
                            draggingFromStatus={draggingFromStatus}
                        />
                    ))}
                </Flex>
            </DragDropContext>

            {/* Modal */}
            <Modal isOpen={!!selectedTicket} onClose={handleCloseModal} size="3xl" isCentered scrollBehavior="inside">
                <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
                <ModalContent borderRadius="xl" overflow="hidden">
                    {isModalLoading ? (
                        <Flex justify="center" align="center" h="300px"><Spinner size="xl" /></Flex>
                    ) : modalDetails && (
                        <>
                            <Box h="4px" bgGradient={statusConfig[modalDetails.status]?.headerGradient || 'linear(to-r, gray.400, gray.500)'} />
                            <ModalHeader fontSize="md" pb={2} pr={12}>
                                <Flex align="center" gap={2} flexWrap="wrap">
                                    <Text fontFamily="mono" color="gray.400" fontSize="sm">#{modalDetails.id}</Text>
                                    <Text>{modalDetails.subject}</Text>
                                    <Button
                                        as={RouterLink}
                                        to={`/tickets/${modalDetails.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        size="xs"
                                        colorScheme="blue"
                                        leftIcon={<FaExternalLinkAlt />}
                                        borderRadius="full"
                                        flexShrink={0}
                                    >
                                        Visualizar Ticket
                                    </Button>
                                </Flex>
                            </ModalHeader>
                            <ModalCloseButton />
                            <ModalBody overflowY="auto" pt={0}>
                                <VStack spacing={4} align="start">
                                    <Flex w="100%" gap={2} flexWrap="wrap">
                                        <Badge colorScheme={priorityConfig[modalDetails.priority]?.color} textTransform="capitalize" borderRadius="full" px={2.5} py={0.5} display="flex" alignItems="center" gap="4px">
                                            <Icon as={priorityConfig[modalDetails.priority]?.icon} boxSize="10px" />{modalDetails.priority}
                                        </Badge>
                                        <Badge colorScheme={statusConfig[modalDetails.status]?.color} textTransform="capitalize" borderRadius="full" px={2.5} py={0.5} display="flex" alignItems="center" gap="4px">
                                            <Icon as={statusConfig[modalDetails.status]?.icon} boxSize="12px" />{statusConfig[modalDetails.status]?.label || modalDetails.status}
                                        </Badge>
                                        {modalDetails.categories?.map(c => (<Tag key={c.id} size="sm" colorScheme="gray" borderRadius="full">{c.name}</Tag>))}
                                    </Flex>
                                    <FormControl>
                                        <FormLabel fontSize="sm" fontWeight="bold">Alterar Status</FormLabel>
                                        <Select value={modalStatus} onChange={(e) => setModalStatus(e.target.value)} size="sm" borderRadius="lg">
                                            <option value="aberto">Aberto</option>
                                            <option value="em_andamento">Em Andamento</option>
                                            <option value="bloqueado">Bloqueado</option>
                                            <option value="fechado">Concluido</option>
                                        </Select>
                                    </FormControl>
                                    <Box w="full">
                                        <Text fontSize="sm" fontWeight="bold" color="gray.500" mb={1}>Atribuido a:</Text>
                                        {modalDetails.assignees?.length > 0 ? (
                                            <HStack flexWrap="wrap" gap={1}>{modalDetails.assignees.map(user => (<Tag key={user.id} size="md" colorScheme="gray" borderRadius="full"><Avatar src={getImgUser(user.id)} size="xs" name={user.name} ml={-1} mr={2} />{user.name}</Tag>))}</HStack>
                                        ) : <Text fontSize="sm" color="gray.400">Ninguem atribuido</Text>}
                                    </Box>
                                    <Divider />
                                    <Box bg="gray.50" p={3} borderRadius="lg" w="100%" border="1px solid" borderColor="gray.100">
                                        {modalDetails.description
                                            ? <WikiContentViewer content={modalDetails.description} />
                                            : <Text color="gray.700" fontSize="sm">Nenhuma descricao fornecida.</Text>}
                                    </Box>
                                    {modalDetails.attachments?.length > 0 && (
                                        <Box w="full">
                                            <Text fontSize="sm" fontWeight="bold" color="gray.500" mb={1}>Anexos:</Text>
                                            <VStack align="start" w="100%">
                                                {modalDetails.attachments.map(file => (
                                                    <Button key={file.id} size="sm" variant="link" colorScheme="blue" leftIcon={<FaDownload />}
                                                        onClick={() => handleDownloadAttachment(file.id, file.name)} isLoading={downloadingId === file.id} isDisabled={downloadingId !== null}
                                                        w="100%" justifyContent="flex-start" style={{ whiteSpace: 'normal', textAlign: 'left', wordBreak: 'break-all' }}>
                                                        <Text noOfLines={1}>{file.name}</Text>
                                                    </Button>
                                                ))}
                                            </VStack>
                                        </Box>
                                    )}
                                </VStack>
                            </ModalBody>
                            <ModalFooter
                                borderTop="1px solid"
                                borderColor="gray.100"
                                flexDirection={{ base: 'column-reverse', sm: 'row' }}
                                justifyContent="space-between"
                                alignItems="stretch"
                                gap={2}
                            >
                                <Button
                                    variant="outline"
                                    colorScheme="orange"
                                    size="sm"
                                    leftIcon={<FaBell />}
                                    onClick={() => setIsNotifyOpen(true)}
                                    borderRadius="lg"
                                    w={{ base: 'full', sm: 'auto' }}
                                >
                                    Notificar
                                </Button>
                                <HStack w={{ base: 'full', sm: 'auto' }} spacing={2}>
                                    <Button variant="ghost" onClick={handleCloseModal} isDisabled={isUpdatingStatus} borderRadius="lg" flex={{ base: 1, sm: 'initial' }}>Cancelar</Button>
                                    <Button colorScheme="blue" onClick={handleUpdateStatus} isLoading={isUpdatingStatus} loadingText="Atualizando..." borderRadius="lg" flex={{ base: 1, sm: 'initial' }}>Atualizar Status</Button>
                                </HStack>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>

            {modalDetails && (
                <TicketNotifyDialog isOpen={isNotifyOpen} onClose={() => setIsNotifyOpen(false)} ticket={modalDetails} />
            )}
        </>
    );
};

export default TicketKanbanView;
